import {
  CreateTaskSchema,
  type CreateTaskInput,
  type TaskDetail,
  type TaskPriority,
} from '@pm/schemas';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Label,
  MarkdownEditor,
  Select,
} from '@pm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ContextSelect,
  TagPicker,
  UserPicker,
  UsersMultiPicker,
  UserChips,
} from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { localInputToIso, PRIORITY_LABEL } from '@/lib/format';
import { usePipelines } from '@/lib/pipelines';
import { useTaskMutation, useUsers, useWorkflowTemplates } from '@/lib/queries';
import { useToast } from '@/lib/toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tarefa pessoal (privada) ou corporativa. */
  scope?: 'CORPORATE' | 'PERSONAL';
  /** Quando informado, cria subtarefa desta mãe. */
  parent?: Pick<TaskDetail, 'id' | 'title' | 'context'>;
  defaultContextId?: string | null;
  /** Ao criar, navega para a tarefa (padrão) ou fica na tela. */
  navigateOnCreate?: boolean;
  /** Pré-seleciona um workflow (pipeline de workflow). */
  defaultWorkflowId?: string;
  /** Pré-seleciona um pipeline manual (quadro aberto). */
  defaultPipelineId?: string;
}

export function TaskCreateDialog({
  open,
  onOpenChange,
  scope = 'CORPORATE',
  parent,
  defaultContextId = null,
  navigateOnCreate = true,
  defaultWorkflowId,
  defaultPipelineId,
}: Props) {
  const me = useMe();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: usersData } = useUsers({ status: 'ACTIVE' });
  const users = usersData?.data ?? [];
  const { data: workflowTemplates = [] } = useWorkflowTemplates();
  const [workflowId, setWorkflowId] = useState(defaultWorkflowId ?? '');
  const manualPipelines = (usePipelines().data ?? []).filter((p) => p.source === 'MANUAL');
  const [pipelineId, setPipelineId] = useState(defaultPipelineId ?? '');

  const personal = scope === 'PERSONAL';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contextId, setContextId] = useState<string | null>(defaultContextId);
  const [responsible, setResponsible] = useState<string | null>(personal ? me.user.id : me.user.id);
  const [participants, setParticipants] = useState<string[]>([]);
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [due, setDue] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isRequired, setIsRequired] = useState(true);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useTaskMutation((input: CreateTaskInput) =>
    parent
      ? api.post<TaskDetail>(`/tasks/${parent.id}/subtasks`, input)
      : workflowId
        ? api.post<TaskDetail>('/workflows/start', {
            workflow_template_id: workflowId,
            context_id: input.context_id ?? null,
            title: input.title,
            responsible_user_id: input.responsible_user_id ?? null,
            due_at: input.due_at ?? null,
            priority: input.priority,
          })
        : api.post<TaskDetail>('/tasks', input),
  );

  function reset() {
    setTitle('');
    setDescription('');
    setParticipants([]);
    setPriority('NORMAL');
    setDue('');
    setRequiresApproval(false);
    setIsRequired(true);
    setTagIds([]);
    setError(null);
  }

  async function submit() {
    const parsed = CreateTaskSchema.safeParse({
      title,
      description: description || undefined,
      scope,
      context_id: parent ? undefined : contextId,
      responsible_user_id: responsible,
      participant_user_ids: personal ? [] : participants.filter((p) => p !== responsible),
      priority,
      due_at: localInputToIso(due),
      requires_approval: personal ? false : requiresApproval,
      is_required: isRequired,
      tag_ids: tagIds,
      pipeline_id: !parent && !personal && !workflowId && pipelineId ? pipelineId : null,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }
    try {
      const created = (await create.mutateAsync(parsed.data)) as TaskDetail;
      toast.success(
        parent ? 'Subtarefa criada' : personal ? 'Tarefa pessoal criada' : 'Demanda criada',
        `#${created.number} ${created.title}`,
      );
      reset();
      onOpenChange(false);
      if (navigateOnCreate && !parent) void navigate(`/tasks/${created.id}`);
    } catch (err) {
      toast.error(err);
    }
  }

  const heading = parent
    ? `Nova subtarefa de #${parent.title}`
    : personal
      ? 'Nova tarefa pessoal'
      : 'Nova demanda';

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : (reset(), onOpenChange(o)))}>
      <DialogContent
        title={heading}
        description={
          personal
            ? 'Privada. Nem administradores veem.'
            : parent
              ? parent.context?.name
              : undefined
        }
        size="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label="Título" htmlFor="t-title" error={error}>
            <Input
              id="t-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="O que precisa ser feito?"
            />
          </Field>

          <div className="flex flex-col gap-1.5">
            <Label>Descrição</Label>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              placeholder="Contexto, links, critérios de aceite…"
              minRows={4}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {!parent && !personal ? (
              <Field label="Contexto" htmlFor="t-ctx">
                <ContextSelect id="t-ctx" value={contextId} onChange={setContextId} />
              </Field>
            ) : null}
            {!personal ? (
              <div className="flex flex-col gap-1.5">
                <Label>Responsável</Label>
                <UserPicker value={responsible} onChange={setResponsible} />
              </div>
            ) : null}
            {!parent && !personal && workflowTemplates.some((t) => t.published) ? (
              <Field
                label="Workflow"
                htmlFor="t-wf"
                hint="Gera as etapas e tarefas da versão publicada."
              >
                <Select
                  id="t-wf"
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                >
                  <option value="">Sem workflow</option>
                  {workflowTemplates
                    .filter((t) => t.published)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} (v{t.published!.version})
                      </option>
                    ))}
                </Select>
              </Field>
            ) : null}
            {!parent && !personal && !workflowId && manualPipelines.length > 0 ? (
              <Field
                label="Pipeline"
                htmlFor="t-pipe"
                hint="Entra na primeira coluna de não iniciadas do quadro."
              >
                <Select
                  id="t-pipe"
                  value={pipelineId}
                  onChange={(e) => setPipelineId(e.target.value)}
                >
                  <option value="">Geral (sem pipeline)</option>
                  {manualPipelines.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Field label="Prioridade" htmlFor="t-prio">
              <Select
                id="t-prio"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
              >
                {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Prazo" htmlFor="t-due">
              <Input
                id="t-due"
                type="datetime-local"
                mono
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </Field>
          </div>

          {!personal ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Label>Participantes</Label>
                <UsersMultiPicker
                  value={participants}
                  onChange={setParticipants}
                  exclude={[responsible]}
                />
              </div>
              <UserChips
                users={users.filter((u) => participants.includes(u.id) && u.id !== responsible)}
                onRemove={(id) => setParticipants(participants.filter((p) => p !== id))}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>Tags</Label>
            <TagPicker value={tagIds} onChange={setTagIds} canManage={can('tasks.manage_tags')} />
          </div>

          <div className="flex flex-wrap gap-5">
            {!personal ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={requiresApproval}
                  onCheckedChange={(v) => setRequiresApproval(v === true)}
                />{' '}
                Exige aprovação para concluir
              </label>
            ) : null}
            {parent ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={isRequired} onCheckedChange={(v) => setIsRequired(v === true)} />{' '}
                Obrigatória para concluir a mãe
              </label>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" loading={create.isPending}>
              Criar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
